import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Portal,
  useDisclosure
} from "@chakra-ui/react"
import { ExternalLinkIcon } from "@pathwright/pathicons"
import useDragSidescroll from "@pathwright/utils/src/scroll/useDragSidescroll"
import { useInView } from "framer-motion"
import { debounce } from "lodash"
import {
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useGalleryContext } from "./Gallery"
import { CarouselProps } from "./types"

// Controls the delay between each item's reveal in seconds
const STAGGER_INTERVAL = 0.1

const DefaultExpander = ({ content }: { content: ReactNode }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <>
      <Box
        pos="absolute"
        display="flex"
        left={0}
        right={0}
        bottom={0}
        alignItems="center"
        justifyContent="center"
        height="3rem"
        pointerEvents="none"
        pb={2}
      >
        <Button
          aria-label="Expand"
          onClick={onOpen}
          rightIcon={<ExternalLinkIcon size="1.4rem" />}
          variant="blurredLightGray"
          cursor="pointer"
          pointerEvents="auto"
          transition="color 0.3s ease-in-out"
        >
          Expand
        </Button>
      </Box>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <Portal>
          <ModalContent borderRadius="card">
            <ModalCloseButton />
            <ModalBody py={10}>
              <Box>{content}</Box>
            </ModalBody>
          </ModalContent>
        </Portal>
      </Modal>
    </>
  )
}

const GalleryCarouselItem = ({
  item,
  itemRefs,
  index,
  staggerEnabled,
  staggerStart,
  initialWidth,
  overflow
}: {
  item: any
  itemRefs: MutableRefObject<HTMLDivElement[]>
  index: number
  staggerEnabled: boolean
  staggerStart: boolean
  initialWidth: string
  overflow: boolean
}) => {
  const renderItem = useGalleryContext((v) => v.renderItem)
  const maxItemHeight = useGalleryContext((v) => v.maxItemHeight)
  const scrollBehavior = useGalleryContext((v) => v.scrollBehavior)
  const renderExpander = useGalleryContext((v) => v.renderExpander)

  const content = useMemo(
    () => renderItem(item, { index }),
    [renderItem, item, index]
  )

  return (
    <Box
      key={index}
      data-index={index}
      ref={(el) => (itemRefs.current[index] = el as HTMLDivElement)}
      className={`carousel-item`}
      scrollSnapAlign={scrollBehavior === "snap" ? "start" : "none"}
      boxSizing="border-box"
      flex="0 0 auto"
      alignSelf="stretch"
      maxH={maxItemHeight}
      pos="relative"
      overflowY="hidden"
      width={initialWidth}
      sx={{
        "& > *:first-of-type": {
          overflowY: "hidden",
          height: "100%"
        }
      }}
    >
      <Box
        className="carousel-item-content"
        h="100%"
        sx={{
          opacity: staggerEnabled ? (staggerStart ? 1 : 0) : 1,
          transform: staggerEnabled
            ? staggerStart
              ? "translateY(0)"
              : "translateY(20px)"
            : "none",
          transition:
            staggerEnabled && staggerStart
              ? `opacity 0.4s cubic-bezier(0.25,0.46,0.45,0.94) ${
                  index * STAGGER_INTERVAL
                }s, transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94) ${
                  index * STAGGER_INTERVAL
                }s`
              : undefined
        }}
      >
        {content}
      </Box>
      {maxItemHeight && overflow ? (
        renderExpander ? (
          renderExpander(item, index)
        ) : (
          <DefaultExpander content={renderItem(item, { index })} />
        )
      ) : null}
    </Box>
  )
}

const GalleryCarousel = <T,>({
  items,
  itemsPerRow = 3,
  // Important note: in order to use carouselPeek, you can't have a parent with horiztonal padding;
  // Instead, use spacingX prop to set the padding on the carousel itself
  carouselPeek,
  scrollBehavior,
  spacingX,
  spacingY,
  maxItemHeight,
  itemsFillWidth,
  gap = 0
}: CarouselProps<T>) => {
  const itemRefs = useGalleryContext((v) => v.itemRefs)
  const enableStagger = useGalleryContext((v) => v.enableStagger)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [overflowByIndex, setOverflowByIndex] = useState<Record<number, boolean>>({})

  // Track when the carousel comes into view for stagger animation
  // Trigger when 50% of the carousel height is visible
  const isInView = useInView(carouselRef, {
    once: true,
    amount: 0.1 // 20% of the element must be visible
  })

  // Record first entry into view; we want items rendered off-screen but hidden, and only reveal + stagger once
  const [hasEnteredView, setHasEnteredView] = useState(false)
  useEffect(() => {
    if (isInView && !hasEnteredView) setHasEnteredView(true)
  }, [isInView, hasEnteredView])
  const staggerStart = hasEnteredView

  const hasOverflow = useMemo(
    () => (items?.length || 0) > itemsPerRow,
    [items, itemsPerRow]
  )

  // Calculate initial item width to prevent narrow cards during loading
  const initialItemWidth = useMemo(() => {
    if (typeof window === "undefined") return "auto"

    // Use a reasonable default width based on viewport and itemsPerRow
    const viewportWidth = window.innerWidth
    const normalizedItemsPerRow = Math.min(
      itemsPerRow,
      items?.length || itemsPerRow
    )

    // Estimate padding (will be refined in useEffect)
    const estimatedPadding = 32 // 16px on each side
    const itemWidth =
      (viewportWidth - estimatedPadding * 2 - normalizedItemsPerRow * gap) /
      normalizedItemsPerRow

    return `${Math.max(itemWidth, 200)}px` // Minimum 200px width
  }, [itemsPerRow, items?.length, gap])

  const {
    handleMouseDown,
    handleTouchStart,
    isDragging,
    isRecentlyDragged,
    resetDragState
  } = useDragSidescroll(scrollerRef, hasOverflow, scrollBehavior)

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const updateItemWidths = () => {
      const pxPadding = parseFloat(
        getComputedStyle(scroller).paddingLeft || "0"
      )
      const visibleWidth = scroller.clientWidth

      // If we have fewer items than itemsPerRow, we want the items to fill the space,
      // so we use that number as our itemsPerRow
      const normalizedItemsPerRow = Math.min(itemsPerRow, items.length)

      // Check if we should use max-content width for items
      const shouldUseMaxContent = !hasOverflow && !itemsFillWidth

      if (shouldUseMaxContent) {
        // Set items to use their natural content width
        itemRefs.current.forEach((item) => {
          if (item) {
            item.style.width = "max-content"
            item.style.flex = "0 0 auto"
          }
        })
        return
      }

      // We set the item width manually to account for viewport size, carousel peek,
      // and overflow (no. of items exceeds itemsPerRow)
      const itemWidth =
        carouselPeek || !hasOverflow
          ? (visibleWidth - pxPadding * 2 - normalizedItemsPerRow * gap) /
            normalizedItemsPerRow
          : (visibleWidth - pxPadding - normalizedItemsPerRow * gap) /
            normalizedItemsPerRow

      itemRefs.current.forEach((item) => {
        if (item) {
          item.style.width = `${itemWidth}px`
          item.style.flex = "0 0 auto"
        }
      })
    }

    // If an item will overflow its container, we want to show an expander button
    const observeItemOverflow = (item: HTMLElement) => {
      const itemHeight = item.clientHeight

      // Get the first child element (the actual content)
      const firstChild = item.firstElementChild as HTMLElement
      if (!firstChild) return

      // Temporarily remove overflow hidden from the first child to measure true content height
      const originalOverflowY = firstChild.style.overflowY

      firstChild.style.overflowY = "visible"

      // Force a reflow to get accurate measurements
      firstChild.offsetHeight

      const contentHeight = firstChild.scrollHeight

      // Restore original overflow immediately
      firstChild.style.overflowY = originalOverflowY

      const idxAttr = item.getAttribute("data-index")
      if (idxAttr == null) return
      const idx = Number(idxAttr)
      const doesOverflow = contentHeight > itemHeight
      setOverflowByIndex((prev) =>
        prev[idx] === doesOverflow ? prev : { ...prev, [idx]: doesOverflow }
      )
    }

    // We need to observe the items for overflow
    // because items may not be measureable when they're not yet scrolled into view
    const overflowObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observeItemOverflow(entry.target as HTMLElement)
            overflowObserver.unobserve(entry.target)
          }
        })
      },
      {
        root: scroller,
        threshold: 0.1
      }
    )

    // Initial setup
    updateItemWidths()
    itemRefs.current.forEach((item) => {
      if (item) overflowObserver.observe(item)
    })

    const debounceHandleItemResize = debounce(() => {
      updateItemWidths()
      // Reobserve if item widths change (could affect number of items on screen)
      itemRefs.current.forEach((item) => {
        if (item) overflowObserver.observe(item)
      })
    }, 100)

    window.addEventListener("resize", debounceHandleItemResize)

    // Re-check overflow when an item is clicked, since content may expand
    const handleItemClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      const itemEl = target.closest(".carousel-item") as HTMLElement | null
      if (!itemEl) return
      // Defer to next tick to allow any click-triggered DOM updates to render
      setTimeout(() => observeItemOverflow(itemEl), 150)
    }

    scroller.addEventListener("click", handleItemClick)

    return () => {
      debounceHandleItemResize.cancel()
      window.removeEventListener("resize", debounceHandleItemResize)
      scroller.removeEventListener("click", handleItemClick)
      overflowObserver.disconnect()
    }
  }, [itemsPerRow, carouselPeek, items])

  return (
    <Box
      ref={carouselRef}
      className="gallery-container"
      width="100%"
      overflowX="hidden"
      pos="relative"
      sx={{
        opacity: hasEnteredView ? 1 : 0,
        transition: "opacity 0.25s ease-out"
      }}
    >
      <Box
        ref={scrollerRef}
        className="carousel-scroller"
        py={spacingY}
        width="100%"
        overflowX="auto"
        scrollSnapType={scrollBehavior === "snap" ? "x mandatory" : "none"}
        scrollPaddingLeft={spacingX}
        scrollPaddingRight={spacingX}
        paddingLeft={spacingX}
        paddingRight={spacingX}
        onMouseDown={hasOverflow ? (e) => handleMouseDown(e) : undefined}
        onTouchStart={hasOverflow ? (e) => handleTouchStart(e) : undefined}
        onBlur={resetDragState}
        tabIndex={hasOverflow ? 0 : -1}
        sx={{
          // Disable text selection when dragging
          userSelect: isDragging ? "none" : "auto",
          // Add smooth scrolling when not dragging
          scrollBehavior: isDragging ? "auto" : "smooth",
          // Prevent pointer events on children during drag and brief timeout after
          "& .carousel-item": {
            pointerEvents: isDragging || isRecentlyDragged ? "none" : "auto"
          },
          // Remove focus outline since this is for drag functionality
          "&:focus": {
            outline: "none"
          }
        }}
      >
        <HStack
          className="carousel-track"
          spacing={`${gap}px`}
          width="max-content"
          maxH={maxItemHeight}
        >
          {items.map((item, index) => (
            <GalleryCarouselItem
              key={`carousel-item-${index}`}
              item={item}
              index={index}
              itemRefs={itemRefs}
              staggerEnabled={Boolean(enableStagger)}
              staggerStart={staggerStart}
              initialWidth={initialItemWidth}
              overflow={Boolean(overflowByIndex[index])}
            />
          ))}
        </HStack>
      </Box>
    </Box>
  )
}

export default GalleryCarousel
